import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { Bell, CheckCircle, Clock } from "react-feather";
import Grid from "../Grid";
import { ResizableScrollingTextRenderer } from "../renderer/StyleRenderer";
import Input from "../input/Input";
import {
  getDriverDetails,
  getNotifications,
  getUnauthorizedStopCounts,
  getUnauthorizedStops,
  saveActionCenter,
} from "../../api/actionCenterApi";
import LoadingSpinner from "../LoadingSpinner";
import { toast } from "react-toastify";
import { getBulkDropdownData } from "../../api/keyvalue";
import Dropdown from "../input/Dropdown";
import { acknowledgeNotification } from "../../api/notificationApi";
import { EmailInput } from "../input/EmailInput";
import FleetDashboardMap from "../maps/Fleet/FleetDashboardMap";
import logoWithoutName from "../../assets/cargotool-icon.png";

const initialFilters = {
  title: "",
  vehicle: "",
  status: "",
  fromDate: "",
  toDate: "",
};
const PAGE_SIZE = 50;

const ActionCenterModal = forwardRef((props, ref) => {
  const user = useSelector((state) => state.user);
  const [event6List, setEvent6List] = useState([]);
  const [customReason, setCustomReason] = useState("");
  const [introMessage, setIntroMessage] = useState(
    "Please find the Unauthorized Stop details below:",
  );
  const [isFullPageLoading, setIsFullPageLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [to, setTo] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [actionType, setActionType] = useState("email");
  const [driverDetails, setDriverDetails] = useState([]);
  const [emailMessage, setEmailMessage] = useState("");
  const [note, setNote] = useState("");
  const [callStatus, setCallStatus] = useState(1);
  const [callType, setCallType] = useState(2);

  const [callTypeOptions, setCallTypeOptions] = useState([]);
  const [callStatusOptions, setCallStatusOptions] = useState([]);
  const [actionTakenOptions, setActionTakenOptions] = useState([]);

  const [page, setPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);

  const [counts, setCounts] = useState({ total: 0, pending: 0, done: 0 });

  const showDetails = !!selected;

  useImperativeHandle(
    ref,
    () => ({
      reset() {
        setFilters(initialFilters);
        setSelected(null);
        setActionType("email");
        setNote("");
        setEmailMessage("");
        setCallStatus(1);
        setCallType(2);
      },
    }),
    [],
  );

  const getBreakdownHtmlTable = (
    driverDetails,
    selected,
    introMessage,
    customReason,
    user,
  ) => {
    const mapsLink = `https://www.google.com/maps?q=${driverDetails.stopLatitude},${driverDetails.stopLongitude}`;
    return `
    <div style="font-family: Arial, sans-serif; font-size: 14px; color: #333;">
        <p>Dear ${driverDetails.empFullname},</p>
        <p>${introMessage || "Please find the incident details below:"}</p>
        <table style="width: 100%; border-collapse: collapse; font-size: 14px; border: 1px solid #ddd;">
            <thead>
                <tr style="background-color: #f7f7f7;">
                    <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Field</th>
                    <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Details</th>
                </tr>
            </thead>
            <tbody>
                ${generateRow("Requesting Code", selected.reqCode || selected.jobCode)}
                ${generateRow("Vehicle Number", selected.vehicle)}
                ${generateRow("Requesting Plant", driverDetails.plantName)}
                ${generateRow("Pick Up Location", driverDetails.startLocation)}
                ${generateRow("Via Location", driverDetails.viaLocations)}
                ${generateRow("Delivery Location", driverDetails.endLocation)}
                ${generateRow("Incident", driverDetails.incidentType || "Unauthorized Stop")}
                ${generateRow("Reason", customReason || driverDetails.incidentReason || "-")}
                ${generateRow("Date & Time", new Date(driverDetails.stopStartTime).toLocaleString("en-US", { timeZone: "Asia/Colombo" }))}
                ${generateRow("Location Link", `<a href="${mapsLink}" target="_blank" style="color: #1677ff; text-decoration: none;">View on Map</a>`)}
                ${generateRow("Monitoring Center Action", user.username)}
            </tbody>
        </table>
        <p style="margin-top: 20px;">Best Regards,<br/>Cargotool Team</p>
    </div>
    `;
  };

  const generateRow = (label, value) => {
    return `
        <tr>
            <td style="padding: 10px; border: 1px solid #ddd; font-weight: 600; background: #fafafa;">${label}</td>
            <td style="padding: 10px; border: 1px solid #ddd;">${value || "-"}</td>
        </tr>
    `;
  };

  const [filters, setFilters] = useState({
    title: "",
    vehicle: "",
    status: null,
    fromDate: "",
    toDate: "",
  });
  const formatLocalDateTime = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      timeZone: "Asia/Colombo",
      dateStyle: "short",
      timeStyle: "short",
    });
  };
  const transformedList = event6List.map((item) => ({
    ...item,
    id: item.notId,
    type: item.notTitle,
    vehicle: item.vehicleNo || item.notVehicleNo || "Unknown",
    jobCode: item.notJobCode || "Unknown",
    jobIdNum: item.notJob ? String(item.notJob) : null,
    reqCode: item.notReqCode || null,
    reqIdNum: item.notReq ? String(item.notReq) : null,
    status:
      item.notActionTaken === "1"
        ? "Pending"
        : item.notActionTaken === "2"
          ? "Email"
          : item.notActionTaken === "3"
            ? "Call"
            : item.notActionTaken === "2,3"
              ? "Email, Call"
              : "Call, Email",
    time: item.notActionTime
      ? new Date(item.notActionTime).toLocaleString()
      : "",
    stopStartTime: formatLocalDateTime(item.stopStartTime),
  }));

  const debounceRef = useRef(null);
  const fetchPage = useCallback(
    async (currentFilters, currentPage) => {
      setIsLoading(true);
      try {
        const payload = {
          vehicleNo: currentFilters.vehicle || null,
          title: currentFilters.title || null,
          status: currentFilters.status || null,
          fromDate: currentFilters.fromDate || null,
          toDate: currentFilters.toDate || null,
          limit: PAGE_SIZE,
          offset: currentPage * PAGE_SIZE,
          currentUser: user.username,
        };

        const response = await getUnauthorizedStops(payload);
        const rows = response?.data || [];
        const total = response?.total ?? 0;

        setEvent6List(rows);
        setTotalRows(total);
      } catch (e) {
        console.error("Failed to fetch unauthorized stops page", e);
        toast.error("Failed to load data", {
          autoClose: 5000,
          containerId: "Modal",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [user.username],
  );

  const fetchCounts = useCallback(
    async (currentFilters) => {
      try {
        const result = await getUnauthorizedStopCounts({
          vehicleNo: currentFilters.vehicle || null,
          title: currentFilters.title || null,
          fromDate: currentFilters.fromDate || null,
          toDate: currentFilters.toDate || null,
          currentUser: user.username,
        });
        setCounts({
          total: result?.total ?? 0,
          pending: result?.pending ?? 0,
          done: result?.done ?? 0,
        });
      } catch (e) {
        console.error("Failed to fetch counts", e);
      }
    },
    [user.username],
  );

  useEffect(() => {
    (async () => {
      setIsFullPageLoading(true);
      try {
        const {
          call_type: callTypeData,
          call_status: callStatusData,
          action_taken: actionTakenData,
        } = await getBulkDropdownData([
          { key: "call_type" },
          { key: "call_status" },
          { key: "action_taken" },
          { key: "status" },
          { key: "active" },
        ]);
        setCallTypeOptions(callTypeData);
        setCallStatusOptions(callStatusData);
        setActionTakenOptions(actionTakenData);

        await fetchPage(initialFilters, 0);
        await fetchCounts(initialFilters);
      } catch (e) {
        console.error("Failed to fetch dropdown data", e);
      } finally {
        setIsFullPageLoading(false);
      }
    })();
  }, []);
  const handleFilterChange = (field, value) => {
    let newValue = value;
    if (field === "status") {
      newValue =
        value === "" || value === null || value === undefined
          ? ""
          : Number(value);
    }
    const updated = { ...filters, [field]: newValue };
    setFilters(updated);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      fetchPage(updated, 0);
      fetchCounts(updated);
    }, 300);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchPage(filters, newPage);
  };

  const handleNotificationClick = async (event) => {
    const item = event?.data;
    setFilters(initialFilters);
    setActionType("email");
    setNote("");
    setEmailMessage("");
    setTo([]);
    setCallStatus(1);
    setCallType(2);
    setIsLoading(true);
    setSelected(item);

    try {
      if (item.jobCode) {
        const payload = { uuid: item.uuid };
        const response = await getDriverDetails(item.jobCode, payload);
        setDriverDetails(response || []);
        setEmailMessage(
          `Dear ${response.empFullname},\n\nI would like to confirm the reason for this stop from ${formatLocalDateTime(response.stopStartTime)} to ${formatLocalDateTime(response.stopEndTime)}.\n\nRequesting Code: ${item.reqCode || item.jobCode}\n\nBest Regards,\nCargotool Team`,
        );
        if (response.empEmail) {
          setTo([response.empEmail]);
        }
      } else {
        console.warn("No jobCode found in item:", item);
      }
    } catch (error) {
      console.error("Error fetching driver details", error);
    } finally {
      setIsLoading(false);
    }
  };
  const resetActionFields = () => {
    setNote("");
    setEmailMessage("");
    setIntroMessage("Please find the Unauthorized Stop details below:");
    setCustomReason("");
    setCallStatus(1);
    setCallType(2);
  };

  const handleSendAction = async () => {
    if (!selected || !driverDetails) return;
    setIsLoading(true);

    const payload = {
      eventId: selected.id,
      eventType: 6,
      jobId: Number(selected.jobIdNum),
      jobCode: selected.jobCode,
      vehicleNumber: selected.vehicle,
      user: user.username,
      actionType: actionType === "email" ? 1 : 2,
      actionTime: new Date(),
      aclNotes: actionType === "email" ? customReason : note,
      callType,
      callStatus,
      emailList: to,
      subject: `Unauthorized stop - ${selected.vehicle} (Requesting Code: ${selected.reqCode || selected.jobCode})`,
      message:
        actionType === "email"
          ? getBreakdownHtmlTable(
              driverDetails,
              selected,
              introMessage,
              customReason,
              user,
            )
          : note,
      companyId: 1,
      longitude: driverDetails.stopLongitude,
      latitude: driverDetails.stopLatitude,
    };

    try {
      const response = await saveActionCenter(payload);
      await acknowledgeNotification({
        username: user.username,
        notId: selected.id,
      });

      if (response.status === 200) {
        toast.success("Successfully submitted action", {
          autoClose: 5000,
          containerId: "Modal",
        });

        await fetchPage(filters, page);
        await fetchCounts(filters);
        setSelected(null);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Failed to save action center", error);
      toast.error("Failed to submit action", {
        autoClose: 5000,
        containerId: "Modal",
      });
      setIsLoading(false);
    }
  };

  return isFullPageLoading ? (
    <LoadingSpinner />
  ) : (
    <div
      ref={ref}
      style={{
        position: "fixed",
        top: 20,
        left: 40,
        width: "95vw",
        height: "95vh",
        borderRadius: "20px",
        background: "#f5f6f7",
        display: "flex",
        flexDirection: "column",
        fontFamily: "sans-serif",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "16px 24px",
          background: "#fff",
          borderBottom: "1px solid #ddd",
        }}
      >
        <img
          src={logoWithoutName}
          alt={"logo"}
          style={{ width: "35px", height: "35px" }}
        />
        <h2
          className={"ui header"}
          style={{ paddingLeft: "10px", fontSize: "20px", margin: 0 }}
        >
          Action Center
        </h2>
      </div>
      <div
        style={{
          display: "flex",
          flex: 1,
          padding: "24px",
          gap: "24px",
          overflow: "auto",
        }}
      >
        <div
          style={{
            flex: showDetails ? 1 : 1.6,
            background: "#fff",
            borderRadius: "12px",
            padding: "16px",
            boxShadow: "0 0 4px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ display: "flex", gap: "24px", marginBottom: "20px" }}>
            {[
              { icon: <Bell size={16} />, label: "Total", value: counts.total },
              {
                icon: <Clock size={16} />,
                label: "Pending",
                value: counts.pending,
              },
              {
                icon: <CheckCircle size={16} />,
                label: "Done",
                value: counts.done,
              },
            ].map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  background: "#f0f2f4",
                  borderRadius: "999px",
                  padding: "8px 24px",
                  paddingLeft: "8px",
                  fontSize: "13px",
                  height: "45px",
                  minWidth: "120px",
                  width: "100%",
                  maxWidth: "160px",
                  justifyContent: "flex-start",
                }}
              >
                <div
                  style={{
                    minWidth: "30px",
                    minHeight: "30px",
                    borderRadius: "50%",
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </div>
                <div style={{ width: "100%" }}>
                  <div style={{ fontSize: "14px", color: "#555" }}>
                    {item.label}{" "}
                    <span style={{ fontWeight: 600, fontSize: "14px" }}>
                      {item.value}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="ui form">
            <div className="field">
              <div className="five fields">
                <Input
                  label="Title"
                  value={filters.title}
                  onChange={(e) => handleFilterChange("title", e.target.value)}
                />
                <Input
                  label="Vehicle"
                  value={filters.vehicle}
                  onChange={(e) =>
                    handleFilterChange("vehicle", e.target.value)
                  }
                />
                <div className="field">
                  <Dropdown
                    className="ui dropdown"
                    value={filters.status}
                    label={"Status"}
                    options={[
                      ...actionTakenOptions,
                      { key: 4, value: 4, text: "Both" },
                    ]}
                    onChange={(e, data) =>
                      handleFilterChange("status", data.value)
                    }
                  />
                </div>
                <Input
                  label="From Date"
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) =>
                    handleFilterChange("fromDate", e.target.value)
                  }
                />
                <Input
                  label="To Date"
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange("toDate", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div
            style={{
              flex: 1,
              overflowY: "auto",
              height: "calc(100vh - 350px)",
              minHeight: "200px",
            }}
          >
            <Grid
              content={transformedList}
              config={[
                { headerName: "Action ID", field: "id" },
                {
                  headerName: "Description",
                  field: "type",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                { headerName: "Vehicle", field: "vehicle" },
                { headerName: "Time", field: "stopStartTime" },
                { headerName: "Status", field: "status" },
                {
                  headerName: "Action Taken By",
                  field: "notActionBy",
                  cellRenderer: ({ value }) => (value ? value : "N/A"),
                },
              ]}
              cellClickHandler={handleNotificationClick}
              multiRowSelection={false}
              pagination={false}
              searchable={false}
              rowHeight={45}
              style={{ width: "100%" }}
              isLoading={isLoading}
            />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: "10px",
              }}
            >
              <span style={{ fontSize: "13px", color: "#666" }}>
                Showing {totalRows === 0 ? 0 : page * PAGE_SIZE + 1}–
                {Math.min((page + 1) * PAGE_SIZE, totalRows)} of {totalRows}
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="ui button"
                  disabled={page === 0 || isLoading}
                  onClick={() => handlePageChange(page - 1)}
                  style={{ padding: "6px 14px", borderRadius: "6px" }}
                >
                  Prev
                </button>
                <button
                  className="ui button"
                  disabled={(page + 1) * PAGE_SIZE >= totalRows || isLoading}
                  onClick={() => handlePageChange(page + 1)}
                  style={{ padding: "6px 14px", borderRadius: "6px" }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        {showDetails &&
          (showMap &&
          driverDetails.stopLatitude &&
          driverDetails.stopLongitude ? (
            <div
              style={{
                flex: 1,
                borderRadius: 12,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <div
                onClick={() => setShowMap(false)}
                style={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  cursor: "pointer",
                  fontSize: "18px",
                  color: "#999",
                  fontWeight: "bold",
                  zIndex: 999,
                }}
                title="Close Map"
              >
                ✖
              </div>
              <FleetDashboardMap
                vehicles={[
                  {
                    vehicleNo: selected.vehicle,
                    lat: driverDetails.stopLatitude,
                    lng: driverDetails.stopLongitude,
                    direction: 0,
                  },
                ]}
                popupVehicle={null}
                setPopupVehicle={() => {}}
                selectedVehicle={null}
                setSelectedVehicle={() => {}}
                onMarkerInfoClick={() => {}}
              />
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                background: "#fff",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 0 4px rgba(0,0,0,0.05)",
                position: "relative",
                overflow: "auto",
              }}
            >
              <div
                onClick={() => setSelected(null)}
                style={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  cursor: "pointer",
                  fontSize: "18px",
                  color: "#999",
                  fontWeight: "bold",
                }}
                title="Close"
              >
                ✖
              </div>
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <LoadingSpinner />
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 12 }}>
                    <span style={{ marginRight: 6, fontSize: "16px" }}>🔘</span>{" "}
                    Action: {selected.type} (ID: {selected.id})
                  </div>
                  <div style={{ marginBottom: 4, fontSize: "14px" }}>
                    <strong>Vehicle:</strong> {selected.vehicle}
                    <span style={{ marginLeft: 24 }}>
                      <strong>Driver:</strong>{" "}
                      {driverDetails.empFullname || "N/A"}
                    </span>
                  </div>
                  <div style={{ marginBottom: 16, fontSize: "14px" }}>
                    <strong>Time:</strong> {selected.time}
                    {driverDetails?.stopLatitude != null &&
                      driverDetails?.stopLongitude != null && (
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowMap(true);
                          }}
                          style={{ marginLeft: 12, color: "#4183c4" }}
                        >
                          View Map
                        </a>
                      )}
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>
                    Suggested Actions:
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      marginBottom: 16,
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: 6,
                      }}
                    >
                      <input
                        type="radio"
                        name="actionType"
                        value="email"
                        checked={actionType === "email"}
                        onChange={() => {
                          setActionType("email");
                          setEmailMessage(
                            `Dear ${driverDetails.empFullname},\n\nI would like to confirm the reason for this stop from ${formatLocalDateTime(driverDetails.stopStartTime)} to ${formatLocalDateTime(driverDetails.stopEndTime)}.\n\nRequesting Code: ${selected.reqCode || selected.jobCode}\n\nBest Regards,\nCargotool Team`,
                          );
                          setNote("");
                        }}
                      />
                      <span>Send Email</span>
                    </label>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <input
                        type="radio"
                        name="actionType"
                        value="call"
                        checked={actionType === "call"}
                        onChange={() => {
                          setActionType("call");
                          setEmailMessage("");
                          setNote("");
                        }}
                      />
                      <span>Call Driver</span>
                    </label>
                  </div>

                  {actionType === "email" && (
                    <div className="ui form">
                      <div className="field" style={{ marginBottom: 12 }}>
                        <label style={{ fontWeight: 500 }}>To:</label>
                        <EmailInput
                          value={to}
                          onChange={(emails) => setTo(emails)}
                        />
                      </div>
                      <div className="field" style={{ marginBottom: 12 }}>
                        <label style={{ fontWeight: 500 }}>Subject:</label>
                        <textarea
                          rows={2}
                          value={`Vehicle ${selected.vehicle} (Requesting Code: ${selected.reqCode || selected.jobCode}) stopped unexpectedly at ${formatLocalDateTime(driverDetails.stopStartTime)}`}
                          readOnly
                          style={{
                            padding: "10px",
                            width: "100%",
                            borderRadius: 6,
                          }}
                        />
                      </div>
                      <div className="field" style={{ marginBottom: 12 }}>
                        <label style={{ fontWeight: 500 }}>
                          Intro Message:
                        </label>
                        <textarea
                          rows={2}
                          value={introMessage}
                          onChange={(e) => setIntroMessage(e.target.value)}
                          style={{
                            padding: "10px",
                            width: "100%",
                            borderRadius: 6,
                          }}
                        />
                      </div>
                      <div className="field" style={{ marginBottom: 12 }}>
                        <label style={{ fontWeight: 500 }}>
                          Incident Reason:
                        </label>
                        <textarea
                          rows={2}
                          value={customReason}
                          onChange={(e) => setCustomReason(e.target.value)}
                          style={{
                            padding: "10px",
                            width: "100%",
                            borderRadius: 6,
                          }}
                        />
                      </div>
                      <div className="field" style={{ marginBottom: 12 }}>
                        <label style={{ fontWeight: 500 }}>
                          Message Preview:
                        </label>
                        <div
                          style={{
                            padding: "10px",
                            width: "100%",
                            borderRadius: 6,
                            border: "1px solid #ddd",
                            background: "#fafafa",
                            maxHeight: "300px",
                            overflowY: "auto",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: getBreakdownHtmlTable(
                              driverDetails,
                              selected,
                              introMessage,
                              customReason,
                              user,
                            ),
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {actionType === "call" && (
                    <div className="ui form">
                      <div className="field" style={{ marginBottom: 12 }}>
                        <label style={{ fontWeight: 500 }}>Phone Number:</label>
                        <input
                          type="text"
                          readOnly={true}
                          placeholder="Enter phone number"
                          value={driverDetails.empMobile || ""}
                          style={{
                            padding: "10px",
                            width: "100%",
                            borderRadius: 6,
                          }}
                        />
                      </div>
                      <div className="field" style={{ marginBottom: 12 }}>
                        <Dropdown
                          label={"Call Status"}
                          options={callStatusOptions}
                          value={callStatus}
                          onChange={(e, data) => setCallStatus(data.value)}
                        />
                      </div>
                      <div className="field" style={{ marginBottom: 12 }}>
                        <Dropdown
                          label={"Call Type"}
                          options={callTypeOptions}
                          value={callType}
                          onChange={(e, data) => setCallType(data.value)}
                        />
                      </div>
                      <div className="field" style={{ marginBottom: 12 }}>
                        <label style={{ fontWeight: 500 }}>Note:</label>
                        <textarea
                          rows={2}
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="Add any relevant notes here"
                          style={{
                            padding: "10px",
                            width: "100%",
                            borderRadius: 6,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div
                    style={{ display: "flex", gap: "12px", marginTop: "16px" }}
                  >
                    <button
                      className="ui primary button"
                      onClick={handleSendAction}
                      disabled={
                        (actionType === "email" && (!to || to.length === 0)) ||
                        (actionType === "call" && !driverDetails.empMobile)
                      }
                      style={{
                        padding: "10px 24px",
                        borderRadius: "6px",
                        fontWeight: 600,
                        backgroundColor: "#1677ff",
                        color: "white",
                      }}
                    >
                      Submit
                    </button>
                    <button
                      className="ui button"
                      onClick={() => {
                        resetActionFields();
                        if (
                          actionType === "email" &&
                          driverDetails.empFullname &&
                          driverDetails.stopStartTime &&
                          driverDetails.stopEndTime
                        ) {
                          setEmailMessage(
                            `Dear ${driverDetails.empFullname},\n\nI would like to confirm the reason for this stop from ${formatLocalDateTime(driverDetails.stopStartTime)} to ${formatLocalDateTime(driverDetails.stopEndTime)}.\n\nRequesting Code: ${selected.reqCode || selected.jobCode}\n\nBest Regards,\nCargotool Team`,
                          );
                          if (driverDetails.empEmail)
                            setTo([driverDetails.empEmail]);
                        }
                      }}
                      style={{
                        padding: "10px 24px",
                        borderRadius: "6px",
                        fontWeight: 600,
                        backgroundColor: "#f0f2f4",
                        color: "#333",
                      }}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
});

export default ActionCenterModal;
